import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  all: [],
  onCases: [],
  onPersons: [],
  onFirms: [],
  onReferrals: [],
  onTreatmentPrograms: [],
  onFacilities: [],
  onMissives: [],
  onOutreaches: [],
  onNotes: [],
  onInsurance: [],
  onVobResolutions: [],
  onStatusTransitions: [],
  onExtensions: [],
  onDischarges: [],
  onInquiries: [],
  onCallRailCalls: [],
};

/*
export const removeNoteOnInquiry = noteId => ({
  type: REMOVE_NOTE,
  payload: {
    noteId,
    category: 'onInquiries',
  },
});
 */

const alreadyHaveNote = (listOfNotes, note) => listOfNotes.some((item) => item.noteId === note.noteId);

const reduceGroup = (state, action, groupName) => {
  const notes = action.payload;
  let all = state.all;
  let groupNotesSoFar = state[groupName];
  notes.forEach((note) => {
    if (alreadyHaveNote(groupNotesSoFar, note)) {
      groupNotesSoFar = groupNotesSoFar.filter((n) => n.noteId !== note.noteId).concat(note);
      all = all.filter((n) => n.noteId !== note.noteId).concat(note);
    } else {
      groupNotesSoFar = [...groupNotesSoFar, note];
      all = [...all, note];
    }
  });
  return { ...state, all, [groupName]: groupNotesSoFar };
};

const removeTheNote = (state, idOfNoteToRemove, groupName) => {
  const filteredAll = state.all.filter((note) => note.noteId !== idOfNoteToRemove);
  const group = state[groupName];
  const filteredGroup = group.filter((note) => note.noteId !== idOfNoteToRemove);
  return { ...state, all: filteredAll, [groupName]: filteredGroup };
};

/*
 * This, notes, houses the Redux representation of all the Notes we've loaded into the system.
 * We maintain a set of lists
 *   * all -- a list of all the Notes we've loaded
 *   * a separate list of notes on each different kind of thing we can have notes on.
 */

export const notesSlice = createSlice({
  name: 'notes',
  initialState: initialState,
  reducers: {
    clearNotes: () => {
      return initialState;
    },

    addOrUpdateNotesOnCases: (state, action) => {
      return reduceGroup(state, action, 'onCases');
    },

    addOrUpdateNotesOnPersons: (state, action) => {
      return reduceGroup(state, action, 'onPersons');
    },

    addOrUpdateNotesOnFirms: (state, action) => {
      return reduceGroup(state, action, 'onFirms');
    },

    addOrUpdateNotesOnReferrals: (state, action) => {
      return reduceGroup(state, action, 'onReferrals');
    },

    addOrUpdateNotesOnTreatmentPrograms: (state, action) => {
      return reduceGroup(state, action, 'onTreatmentPrograms');
    },

    addOrUpdateNotesOnFacilities: (state, action) => {
      return reduceGroup(state, action, 'onFacilities');
    },

    addOrUpdateNotesOnMissives: (state, action) => {
      return reduceGroup(state, action, 'onMissives');
    },

    addOrUpdateNotesOnOutreaches: (state, action) => {
      return reduceGroup(state, action, 'onOutreaches');
    },

    addOrUpdateNotesOnNotes: (state, action) => {
      return reduceGroup(state, action, 'onNotes');
    },

    addOrUpdateNotesOnInsurance: (state, action) => {
      return reduceGroup(state, action, 'onInsurance');
    },

    addOrUpdateNotesOnVobResolutions: (state, action) => {
      return reduceGroup(state, action, 'onVobResolutions');
    },

    addOrUpdateNotesOnStatusTransitions: (state, action) => {
      return reduceGroup(state, action, 'onStatusTransitions');
    },

    addOrUpdateNotesOnExtensions: (state, action) => {
      return reduceGroup(state, action, 'onExtensions');
    },

    addOrUpdateNotesOnDischarges: (state, action) => {
      return reduceGroup(state, action, 'onDischarges');
    },

    addOrUpdateNotesOnInquiries: (state, action) => {
      return reduceGroup(state, action, 'onInquiries');
    },

    addOrUpdateNotesOnCallRailCalls: (state, action) => {
      return reduceGroup(state, action, 'onCallRailCalls');
    },

    removeNoteOnPerson: (state, action) => {
      return removeTheNote(state, action.payload, 'onPersons');
    },

    removeNoteOnReferral: (state, action) => {
      return removeTheNote(state, action.payload, 'onReferrals');
    },

    removeNoteOnInquiry: (state, action) => {
      return removeTheNote(state, action.payload, 'onInquiries');
    },
  },
});

export const {
  clearNotes,
  addOrUpdateNotesOnCases,
  addOrUpdateNotesOnPersons,
  addOrUpdateNotesOnFirms,
  addOrUpdateNotesOnReferrals,
  addOrUpdateNotesOnTreatmentPrograms,
  addOrUpdateNotesOnFacilities,
  addOrUpdateNotesOnOutreaches,
  addOrUpdateNotesOnMissives,
  addOrUpdateNotesOnNotes,
  addOrUpdateNotesOnInsurance,
  addOrUpdateNotesOnVobResolutions,
  addOrUpdateNotesOnStatusTransitions,
  addOrUpdateNotesOnExtensions,
  addOrUpdateNotesOnDischarges,
  addOrUpdateNotesOnInquiries,
  addOrUpdateNotesOnCallRailCalls,
  removeNoteOnPerson,
  removeNoteOnReferral,
  removeNoteOnInquiry,
} = notesSlice.actions;

export default notesSlice.reducer;
